import { useCallback, useState, useEffect, useRef } from 'react';
import auth from '../auth';
import { onAuthStateChanged } from 'firebase/auth';
import firestore from '../firestore';
import { embedded_sale_url, get_embedded_sale_context, LOGIN_PATH } from '../utils/embeddedSale';
import {
	clearImpersonationSession,
	getActiveImpersonationSession,
	getImpersonationSession,
	isImpersonationExpired,
	saveImpersonationExpiredMessage
} from '../utils/impersonation';

export interface UserData {
	delinquency?: number;
	firebase_token: string;
	email: string;
	limit_reached: boolean;
	flexible_installments: boolean;
	last_modified_date: {
		seconds: number;
		nanoseconds: number;
	};
	is_active: boolean;
	master_user: boolean;
	is_seller?: boolean;
	seller_user?: boolean;
	seller_commissions_access?: boolean;
	only_allow_tonic: boolean;
	krece_fixed_fee: number;
	allow_any_product: boolean;
	master_store: Omit<UserData, 'firebase_token' | 'master_store'> & { id: string } & {
		seller_permissions_blacklist?: { [key: string]: string[] };
		seller_permissions_whitelist?: { [key: string]: string[] };
	};
	online_sales_access?: boolean;
	online_sales_beta_access?: boolean;
	'2fa_needed': boolean;
	'2fa_phone_number'?: string;
	store_financed: boolean;
	down_payment: number;
	security_provider: string;
	subStoreAdminPhoneNumbers: { [key: string]: string };
	krece_fee: number;
	self_financed: boolean;
	sub_stores: string[];
	principal_store: string;
	allow_online: boolean;
	allow_iphone?: boolean;
	phone_number?: string;
	store_name: string;
	online_store_name?: string;
	zelleAccounts: string[];
	financed_by_krece: boolean;
	cobranza_access: boolean;
	cobranza_portal_access?: boolean;
	promotions_portal_access?: boolean;
	country: 'venezuela' | string;
	registration_steps?: {
		terms_accepted?: boolean;
		company_info?: boolean;
		bank_created?: boolean;
		sucursal_created?: boolean;
		docusign_accepted?: boolean;
		registration_completed?: boolean;
	};
	access_to_other_stores?: boolean;
	access_type?:
		| 'ata'
		| 'atc'
		| 'admin'
		| 'admin_finanzas'
		| 'tecnoplan'
		| 'postventa'
		| 'manager'
		| 'supervisor'
		| 'pagos'
		| 'finanzas'
		| 'finanzas_limitado'
		| 'marketing';
	access_countries?: string[];
	access_routes?: {
		route: string;
		countries: string[];
	}[];
	bank_account?: {
		account_name?: string;
		account_number?: string;
		account_type?: string;
		bank_name?: string;
		document_type?: string;
		identification_number?: string;
		interbank_code?: string;
	};
	recarga_sales_access?: boolean;
	stores_info?: {
		[brand: string]: {
			sucursales?: {
				location?: string;
				location_details?: string;
				location_coordinates?: {
					latitude: number;
					longitude: number;
				};
				store_name?: string;
				is_active?: boolean;
			}[];
		};
	};
}

export default function useAuth() {
	const [authenticated, setAuthenticated] = useState(false);
	const [shouldLogin, setShouldLogin] = useState(false);
	const [userData, setUserData] = useState<UserData | null>(null);
	const [internal, setInternal] = useState(false);
	const [accessToOtherStores, setAccessToOtherStores] = useState<any>(null);
	const [newVersionAvailable, setNewVersionAvailable] = useState<boolean>(false);
	const [appSettingsApkUrl, setAppSettingsApkUrl] = useState<string | null>(null);
	const [adminAccounts, setAdminAccounts] = useState<string[]>([]);
	const registrationStepsRef = useRef<UserData['registration_steps'] | null>(null);
	const userRef = useRef<{ user: any } | null>(null);

	const expireImpersonationSession = useCallback(() => {
		clearImpersonationSession();
		saveImpersonationExpiredMessage();
		setAccessToOtherStores(null);
		auth.signOut().catch(() => {
			console.log('Error al cerrar sesión expirada de Logear como');
		});
	}, []);

	useEffect(() => {
		let active = true;
		let authVersion = 0;
		let retryTimer: ReturnType<typeof setTimeout> | undefined;

		const clearUser = () => {
			setAuthenticated(false);
			setUserData(null);
			setInternal(false);
			setAccessToOtherStores(null);
			userRef.current = null;
		};

		const unsubscribe = onAuthStateChanged(auth, (user) => {
			const version = ++authVersion;
			if (retryTimer) clearTimeout(retryTimer);
			clearUser();
			setShouldLogin(false);
			let retryDelay = 2000;

			const loadUser = async () => {
				try {
					if (!user?.email) throw new Error();

					const savedImpersonation = getImpersonationSession();
					if (
						savedImpersonation?.targetEmail.toLowerCase() === user.email.toLowerCase() &&
						isImpersonationExpired(savedImpersonation)
					) {
						expireImpersonationSession();
						throw new Error();
					}
					const activeImpersonation = getActiveImpersonationSession(user.email);

					const data = (await firestore.collection('Stores').doc(user.email).get())?.data() as
						| undefined
						| Omit<UserData, 'email' | 'firebase_token'>;
					if (!data) throw new Error();

					let master_data: Omit<UserData, 'email' | 'firebase_token' | 'master_store'> | undefined;
					if (data?.master_store?.id && user.email !== data.master_store.id) {
						master_data = (await firestore.collection('Stores').doc(data.master_store.id).get())?.data() as
							| undefined
							| Omit<UserData, 'email' | 'firebase_token'>;
						if (!master_data) throw new Error();
					} else if (data?.master_store?.id && user.email === data.master_store.id) {
						const { master_store, ...rest } = data;
						master_data = { ...rest };
					}

					if (master_data) {
						data.master_store = { ...master_data, id: data.master_store.id, email: data.master_store.id };
					}

					const firebase_token = await user.getIdToken();
					if (!active || version !== authVersion) return;
					setShouldLogin(false);
					setAuthenticated(true);
					userRef.current = { user };
					// if (data) data.country = "rd";
					if (data?.sub_stores?.length) data.sub_stores.sort();
					setUserData({
						firebase_token,
						email: user.email,
						...data
					} satisfies UserData);
					setInternal(['ata', 'atc', 'admin', 'admin_finanzas', 'postventa', 'tecnoplan', 'manager', 'pagos', 'marketing', 'finanzas', 'finanzas_limitado'].includes(data?.access_type || ''));
					setAccessToOtherStores(
						activeImpersonation
							? {
									token: activeImpersonation.originalToken,
									email: activeImpersonation.originalEmail,
									targetEmail: activeImpersonation.targetEmail,
									startedAt: activeImpersonation.startedAt,
									expiresAt: activeImpersonation.expiresAt,
									isImpersonating: true
								}
							: data?.access_to_other_stores
								? { token: firebase_token, email: user.email }
								: null
					);
					if (data.registration_steps) registrationStepsRef.current = data.registration_steps;
				} catch (error) {
					if (!active || version !== authVersion) return;
					clearUser();
					if (user?.email && isTransientAuthError(error)) {
						console.warn('No se pudo verificar la cuenta. Reintentando.', error);
						retryTimer = setTimeout(loadUser, retryDelay);
						retryDelay = Math.min(retryDelay * 2, 30000);
						return;
					}
					setShouldLogin(true);
				}
			};

			void loadUser();
		});

		return () => {
			active = false;
			authVersion++;
			if (retryTimer) clearTimeout(retryTimer);
			unsubscribe();
		};
	}, [expireImpersonationSession]);

	useEffect(() => {
		if (!accessToOtherStores?.isImpersonating || !accessToOtherStores?.expiresAt) return;

		const expiresIn = accessToOtherStores.expiresAt - Date.now();
		if (expiresIn <= 0) {
			expireImpersonationSession();
			return;
		}

		const timeoutId = window.setTimeout(expireImpersonationSession, expiresIn);
		return () => window.clearTimeout(timeoutId);
	}, [accessToOtherStores?.expiresAt, accessToOtherStores?.isImpersonating, expireImpersonationSession]);

	// Refresh token every minute
	// useEffect(() => {
	// 	if (!authenticated || !userRef.current?.user) return;

	// 	const refreshToken = async () => {
	// 		try {
	// 			const user = userRef.current?.user;
	// 			if (!user) return;

	// 			const firebase_token = await user.getIdToken(true); // Force refresh
	// 			if (userData) {
	// 				setUserData({
	// 					...userData,
	// 					firebase_token
	// 				});

	// 				// Update access to other stores token if needed
	// 				if (userData.access_to_other_stores) {
	// 					setAccessToOtherStores({
	// 						token: firebase_token,
	// 						email: userData.email
	// 					});
	// 				}

	// 				console.log('Token refreshed');
	// 			}
	// 		} catch (error) {
	// 			console.error('Error refreshing token:', error);
	// 		}
	// 	};

	// 	const intervalId = setInterval(refreshToken, 60000); // 60000ms = 1 minute

	// 	return () => clearInterval(intervalId);
	// }, [authenticated, userData]);

	useEffect(() => {
		const email = userData?.email;
		if (!email) return;

		const unsubscribeStore = firestore
			.collection('Stores')
			.doc(email)
			.onSnapshot((doc) => {
				try {
					const data = doc.data() as undefined | Omit<UserData, 'email' | 'firebase_token'>;
					if (!data) return;

					const registrationStepsChanged =
						!!data.registration_steps && !areRegistrationStepsEqual(registrationStepsRef.current, data.registration_steps);
					if (registrationStepsChanged) registrationStepsRef.current = data.registration_steps || null;

					setUserData((current) => {
						if (!current || current.email !== email) return current;
						const commissionAccessChanged = current.seller_commissions_access !== data.seller_commissions_access;
						if (!registrationStepsChanged && !commissionAccessChanged) return current;
						return {
							...current,
							...(registrationStepsChanged && { registration_steps: data.registration_steps }),
							...(commissionAccessChanged && { seller_commissions_access: data.seller_commissions_access })
						} satisfies UserData;
					});
				} catch (error) {}
			});

		return () => unsubscribeStore();
	}, [userData?.email]);

	let app_version: number | null | undefined = undefined;
	useEffect(() => {
		(async () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			app_version = (await firestore.collection('AppSetting').doc('AppSettings').get())?.data()?.app_version;
		})();
	}, []);

	useEffect(() => {
		const unsubscribeStore = firestore
			.collection('AppSetting')
			.doc('AppSettings')
			.onSnapshot(async (doc) => {
				try {
					const data = doc.data();
					const version = data?.app_version;
					if (typeof version === 'number' && typeof app_version === 'number' && version > app_version) {
						setTimeout(() => {
							setNewVersionAvailable(true);
						}, 60000);
					}
					setAppSettingsApkUrl(data?.apk_url || null);
					setAdminAccounts(Array.isArray(data?.admin_accounts) ? data.admin_accounts : []);
				} catch (error) {}
			});

		return () => unsubscribeStore();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isAdminAccount =
		userData?.access_type === 'admin' ||
		adminAccounts.some((adminEmail) => adminEmail.toLowerCase() === userData?.email?.toLowerCase());

	return {
		authenticated,
		userData,
		shouldLogin,
		internal,
		accessToOtherStores,
		newVersionAvailable,
		appSettingsApkUrl,
		isAdminAccount
	};
}

export function logout(callback?: () => void) {
	const embedded = get_embedded_sale_context();
	const login_url = embedded ? embedded_sale_url(LOGIN_PATH, embedded) : null;
	clearImpersonationSession();
	auth
		.signOut()
		.catch(() => {
			console.log('Error al cerrar sesión');
		})
		.finally(() => {
			if (callback) callback();
			else if (login_url) window.location.replace(login_url);
		});
}

function isTransientAuthError(error: unknown): boolean {
	if (!error || typeof error !== 'object' || !('code' in error) || typeof error.code !== 'string') return false;
	return ['unavailable', 'deadline-exceeded', 'auth/network-request-failed'].includes(error.code);
}

function areRegistrationStepsEqual(
	steps1: UserData['registration_steps'] | null,
	steps2: UserData['registration_steps'] | null
): boolean {
	if (!steps1 || !steps2) return steps1 === steps2;

	const keys = Object.keys(steps1) as Array<keyof typeof steps1>;
	return keys.every((key) => steps1[key] === steps2[key]);
}
